import { useCheckCreditCardMembershipRequirementQuery } from "@lending/hooks/queries/CreditCardApplication/useCheckCreditCardMembershipRequirementQuery";
import { useGetCreditCardApplicationQuery } from "@lending/hooks/queries/CreditCardApplication/useGetCreditCardApplicationQuery";
import { useListCreditCardOffersQuery } from "@lending/hooks/queries/CreditCardOffer/useListCreditCardOffersQuery";
import { useCreateCreditCardOfferSelectionQuery } from "@lending/hooks/queries/CreditCardOfferSelection/useCreateCreditCardOfferSelectionQuery";
import { useApiErrorToast } from "@lending/hooks/useApiErrorToast";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useFlowNavigation } from "@lending/navigation";
import { navigateToMembershipShareStartStep } from "@lending/pages/Common/MembershipShare/hooks/navigateToMembershipShareStartStep";

function useOfferContainer() {
  const { goToPath, goToStep } = useFlowNavigation();
  const lendingApplicationID = useLendingApplicationID();
  const handleApiError = useApiErrorToast();

  const { data: offersData, isLoading: isOffersDataLoading } = useListCreditCardOffersQuery();
  const {
    data: application,
    isLoading: isApplicationLoading,
    refetch: refetchApplication,
  } = useGetCreditCardApplicationQuery();

  const navigateToSubmitted = () => {
    goToStep("submitted");
  };

  const routeFromRecoveredApplicationState = async (state?: string) => {
    if (!state) {
      handleApiError(undefined);
      return;
    }

    if (state === "offer_accepted") {
      return;
    }

    if (state === "membership_share_pending" || state === "membership_selection") {
      await navigateToMembershipShareStartStep({
        applicationType: "credit-card",
        lendingApplicationID,
        state,
        verificationRequired: false,
        goToPath,
      });
      return;
    }

    navigateToSubmitted();
  };

  const recoverFromMembershipCheckError = async () => {
    try {
      const { data: refreshedApplication } = await refetchApplication();

      await routeFromRecoveredApplicationState(refreshedApplication?.state);
    } catch {
      handleApiError(undefined);
    }
  };

  const { mutateAsync: checkMembershipRequirement, isPending: isCheckingMembership } =
    useCheckCreditCardMembershipRequirementQuery({
      onSuccess: async ({
        state,
        membership_share_verification_required: verificationRequired,
        membership_share_email_conflict: emailConflict,
      }) => {
        if (state === "membership_share_pending" || state === "membership_selection") {
          await navigateToMembershipShareStartStep({
            applicationType: "credit-card",
            lendingApplicationID,
            state,
            verificationRequired,
            emailConflict,
            goToPath,
          });
        } else {
          navigateToSubmitted();
        }

        return Promise.resolve();
      },
      onError: () => {
        void recoverFromMembershipCheckError();
      },
    });

  const { mutateAsync: createOfferSelection, isPending: isAcceptingOffer } =
    useCreateCreditCardOfferSelectionQuery({
      onSuccess: async () => {
        if (application) {
          // @ts-expect-error schema incorrectly requires body for this action
          await checkMembershipRequirement({
            params: { path: { uuid: application.id } },
          });
        }
      },
      onError: handleApiError,
    });

  const isLoading = isApplicationLoading || isOffersDataLoading;
  const isPending = isAcceptingOffer || isCheckingMembership;

  const primaryApplicantFirstName = application?.primary_applicant?.first_name ?? "";
  const coApplicantFirstName = application?.additional_applicants?.[0]?.first_name ?? "";

  const offer = offersData?.results[0];

  async function acceptOffer() {
    if (!offer) return;

    try {
      if (application?.state === "offer_accepted") {
        // @ts-expect-error schema incorrectly requires body for this action
        await checkMembershipRequirement({
          params: { path: { uuid: lendingApplicationID } },
        });
        return;
      }

      await createOfferSelection({
        // @ts-expect-error openapi-typescript does not respect readonly payload fields
        body: {
          application: lendingApplicationID,
          product_offer: offer.id,
        },
      });
    } catch {
      // onError surfaces the failure in a toast; swallow the rethrow so the
      // promise from the click handler does not become an unhandled rejection.
    }
  }

  return {
    isLoading,
    isPending,
    primaryApplicantFirstName,
    coApplicantFirstName,
    offer,
    acceptOffer,
  };
}

export { useOfferContainer };
